import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FiAlertTriangle, FiCheckCircle, FiSend, FiX } from "react-icons/fi";
import { ModalFrame, T } from "../Sales/SalesShared";
import { getCustomersByName, getSuppliersByName } from "../../_services/contact.service";
import { sendDocumentEmail } from "../../_services/documentEmail.service";

/* "Send" for Sales / Purchasing / Finance documents: emails each document to
   its customer or supplier with a PDF made from the same print layout as
   Print / Save PDF. Used by the Send button on a document and by
   "Send Selected" on the lists (one email per document). */

// The server's name for each document type that can be sent (see
// documentEmailApi.js). Purchase Invoices are the supplier's own invoices.
export const DOCUMENT_EMAIL_TYPES = {
    "Quotation": "quotation",
    "Sales Order": "sales_order",
    "Delivery Order": "delivery_order",
    "Invoice": "invoice",
    "Credit Note": "credit_note",
    "Debit Note": "debit_note",
    "Purchase Order": "purchase_order",
    "Purchase Credit Note": "purchase_credit_note",
    "Purchase Debit Note": "purchase_debit_note",
    "Payment Voucher": "payment_voucher",
};

export const canSendDocumentType = (docType) => Boolean(DOCUMENT_EMAIL_TYPES[docType]);
// Same rule as the Send button: Approved documents only.
export const isSendable = (row) => row.status === "Approved";

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;
const splitEmails = (value) => String(value || "").split(/[,;]/).map((part) => part.trim()).filter(Boolean);
const EMAIL_PATTERN = /^[^\s@,;<>"()]+@[^\s@,;<>"()]+\.[^\s@,;<>"()]+$/;
const badEmails = (value) => splitEmails(value).filter((address) => !EMAIL_PATTERN.test(address));

export const DEFAULT_MESSAGE =
    "Dear Sir/Madam,\n\nPlease find the attached document for your reference.\n\nThank you.";

export const defaultSubject = (docType, id, companyName) =>
    `${docType} ${id}${companyName ? ` from ${companyName}` : ""}`;

/* The contact person's email from Customer / Supplier Maintenance; falls
   back to another contact of the same company that has one. */
export async function findContactEmail(party, company, contactPerson) {
    if (!company) return "";
    const lookup = party === "supplier" ? getSuppliersByName : getCustomersByName;
    const rows = await lookup(company).catch(() => []);
    const same = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
    const companyRows = (rows || []).filter((row) => same(row.name, company));
    const contact = companyRows.find((row) => same(row.attention, contactPerson) && row.email);
    const chosen = contact || companyRows.find((row) => row.email);
    return chosen ? [chosen.email, chosen.email2].filter(Boolean).join(", ") : "";
}

/* The document's print layout as a PDF (base64, no data: prefix). */
export async function makeDocumentPdf(node, fileName) {
    const { default: html2pdf } = await import("html2pdf.js");
    const dataUri = await html2pdf()
        .set({
            margin: 0,
            filename: fileName,
            image: { type: "jpeg", quality: 0.95 },
            html2canvas: {
                scale: 2,
                useCORS: true,
                // The on-screen preview has a shadow and centring margin; the PDF doesn't.
                onclone: (doc) => doc.querySelectorAll(".print-page").forEach((page) => {
                    page.style.boxShadow = "none";
                    page.style.margin = "0";
                }),
            },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            pagebreak: { mode: ["css", "legacy"] },
        })
        .from(node)
        .outputPdf("datauristring");
    return String(dataUri).replace(/^data:application\/pdf;[^,]*,/, "");
}

const inputStyle = {
    width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 13,
    border: `1px solid ${T.border}`, borderRadius: T.radiusSm, fontFamily: "inherit",
};
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: T.textMuted, margin: "10px 0 4px", textAlign: "left" };

/**
 * docs: full documents (as the document page has them), all of one docType.
 * onDone({ type, msg }) after a run; the dialog closes itself when every
 * document was sent and stays open on the failed ones otherwise.
 */
export function SendDocumentDialog({
    docs, docType, party, Template, companyProfile, forceNegativeTotals = false, onClose, onDone,
}) {
    const single = docs.length === 1;
    const companyName = companyProfile?.CompanyName || "";
    const partyOf = (doc) => (party === "supplier" ? doc.supplier : doc.customer);

    const [rows, setRows] = useState(() => docs.map((doc) => ({ doc, to: "", state: "pending", error: "" })));
    const [lookingUp, setLookingUp] = useState(true);
    const [cc, setCc] = useState("");
    const [subject, setSubject] = useState(single ? defaultSubject(docType, docs[0].id, companyName) : "");
    const [message, setMessage] = useState(DEFAULT_MESSAGE);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);
    const [formError, setFormError] = useState("");
    const pageRefs = useRef({});
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    // Fill in each contact's email.
    useEffect(() => {
        let alive = true;
        Promise.all(docs.map((doc) => findContactEmail(party, partyOf(doc), doc.contactPerson)))
            .then((emails) => {
                if (!alive) return;
                setRows((current) => current.map((row, index) => ({ ...row, to: row.to || emails[index] || "" })));
            })
            .finally(() => { if (alive) setLookingUp(false); });
        return () => { alive = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const setRowTo = (index, to) =>
        setRows((current) => current.map((row, i) => (i === index ? { ...row, to } : row)));

    const toSend = rows.filter((row) => row.state !== "sent");

    const send = async () => {
        if (busy) return;
        const problems = [];
        toSend.forEach((row) => {
            if (!splitEmails(row.to).length) problems.push(`${row.doc.id}: enter an email address`);
            else if (badEmails(row.to).length) problems.push(`${row.doc.id}: not a valid email (${badEmails(row.to).join(", ")})`);
        });
        if (badEmails(cc).length) problems.push(`CC: not a valid email (${badEmails(cc).join(", ")})`);
        if (single && !subject.trim()) problems.push("Enter a subject");
        if (problems.length) {
            setFormError(problems.join(". ") + ".");
            return;
        }
        setFormError("");
        setBusy(true);
        setProgress(0);

        const sent = [];
        const failed = [];
        let done = 0;
        for (const row of toSend) {
            const id = row.doc.id;
            try {
                const pdfBase64 = await makeDocumentPdf(pageRefs.current[id], `${id}.pdf`);
                await sendDocumentEmail({
                    documentType: DOCUMENT_EMAIL_TYPES[docType],
                    documentId: id,
                    to: splitEmails(row.to),
                    cc: splitEmails(cc),
                    subject: single ? subject.trim() : defaultSubject(docType, id, companyName),
                    message,
                    pdfBase64,
                });
                sent.push(row);
                if (mounted.current) setRows((current) => current.map((r) => (r.doc.id === id ? { ...r, state: "sent", error: "" } : r)));
            } catch (error) {
                failed.push({ row, error });
                if (mounted.current) setRows((current) => current.map((r) => (r.doc.id === id ? { ...r, state: "failed", error: error?.message || "Could not send." } : r)));
            }
            done += 1;
            if (mounted.current) setProgress(done);
        }
        if (mounted.current) setBusy(false);

        const parts = [];
        if (sent.length) parts.push(`${plural(sent.length, docType)} sent: ${sent.map((r) => `${r.doc.id} to ${splitEmails(r.to).join(", ")}`).join("; ")}`);
        if (failed.length) parts.push(`${plural(failed.length, docType)} not sent: ${failed.map(({ row, error }) => `${row.doc.id} (${error?.message || "unknown error"})`).join("; ")}`);
        onDone?.({ type: failed.length ? "danger" : "success", msg: `${parts.join(". ")}.` });
        if (!failed.length) onClose();
    };

    const close = () => { if (!busy) onClose(); };

    return <>
        {ReactDOM.createPortal(
        <ModalFrame onCancel={close} labelledBy="send-document-title">
            <button className="cl-modal-close" onClick={close} disabled={busy} aria-label="Close dialog"><FiX size={14} /></button>
            <div className="cl-modal-icon" style={{ background: "#eff6ff", color: T.accent }}>
                <FiSend size={24} />
            </div>
            <p className="cl-modal-msg" id="send-document-title">
                {busy
                    ? <>Sending… <strong>{progress} of {toSend.length}</strong></>
                    : single
                        ? <>Send <strong>{docType} {docs[0].id}</strong></>
                        : <>Send <strong>{plural(docs.length, docType)}</strong></>}
            </p>

            <div style={{ maxHeight: "55vh", overflowY: "auto", textAlign: "left", marginBottom: 12 }}>
                {single ? (
                    <>
                        <label style={labelStyle} htmlFor="send-to">To</label>
                        <input
                            id="send-to"
                            style={inputStyle}
                            value={rows[0].to}
                            placeholder={lookingUp ? "Looking up the contact's email…" : "name@company.com"}
                            onChange={(e) => setRowTo(0, e.target.value)}
                            disabled={busy}
                        />
                        {!lookingUp && !rows[0].to && (
                            <div style={{ fontSize: 12, color: T.warning, marginTop: 4 }}>
                                No email saved for {partyOf(docs[0]) || "this contact"}. Type one in.
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div style={labelStyle}>To (each document goes to its own contact)</div>
                        {rows.map((row, index) => (
                            <div key={row.doc.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <div style={{ width: 150, flexShrink: 0, fontSize: 12 }}>
                                    <div style={{ fontWeight: 700, color: T.textPrimary }}>{row.doc.id}</div>
                                    <div style={{ color: T.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{partyOf(row.doc)}</div>
                                </div>
                                <input
                                    aria-label={`Email for ${row.doc.id}`}
                                    style={{ ...inputStyle, flex: 1 }}
                                    value={row.to}
                                    placeholder={lookingUp ? "Looking up…" : "No email saved — type one"}
                                    onChange={(e) => setRowTo(index, e.target.value)}
                                    disabled={busy || row.state === "sent"}
                                />
                                <span style={{ width: 18, flexShrink: 0 }} title={row.error || undefined}>
                                    {row.state === "sent" && <FiCheckCircle color={T.success} aria-label="Sent" />}
                                    {row.state === "failed" && <FiAlertTriangle color={T.danger} aria-label={`Not sent: ${row.error}`} />}
                                </span>
                            </div>
                        ))}
                    </>
                )}

                <label style={labelStyle} htmlFor="send-cc">CC (optional)</label>
                <input id="send-cc" style={inputStyle} value={cc} onChange={(e) => setCc(e.target.value)} disabled={busy} placeholder="Separate several addresses with commas" />

                {single ? (
                    <>
                        <label style={labelStyle} htmlFor="send-subject">Subject</label>
                        <input id="send-subject" style={inputStyle} value={subject} onChange={(e) => setSubject(e.target.value)} disabled={busy} maxLength={200} />
                    </>
                ) : (
                    <div style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>
                        Subject: e.g. “{defaultSubject(docType, docs[0].id, companyName)}”
                    </div>
                )}

                <label style={labelStyle} htmlFor="send-message">Message</label>
                <textarea
                    id="send-message"
                    style={{ ...inputStyle, resize: "vertical" }}
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={busy}
                    maxLength={5000}
                />
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>
                    The {single ? "document is" : "documents are"} attached as PDF{single ? ` (${docs[0].id}.pdf)` : "s"}.
                </div>

                {single && rows[0].state === "failed" && (
                    <div className="cl-modal-warning" role="alert" style={{ marginTop: 10 }}>
                        <div className="cl-modal-warning-title"><FiAlertTriangle size={12} /> Not sent</div>
                        <div className="cl-modal-warning-text">{rows[0].error}</div>
                    </div>
                )}
                {formError && (
                    <div role="alert" style={{ marginTop: 10, fontSize: 13, color: T.danger }}>{formError}</div>
                )}
            </div>

            <div className="cl-modal-actions">
                <button className="cl-modal-btn-cancel" onClick={close} disabled={busy}>Cancel</button>
                <button
                    className="cl-modal-btn-confirm"
                    style={{ background: T.accent }}
                    onClick={send}
                    disabled={busy || lookingUp || toSend.length === 0}
                >
                    <FiSend size={13} /> {busy ? "Sending…" : rows.some((row) => row.state === "failed") ? "Retry" : "Send"}
                </button>
            </div>
        </ModalFrame>,
        document.body
        )}

        {/* The print layouts the PDFs are made from: on the page (so they can be
            drawn) but out of sight. */}
        {ReactDOM.createPortal(
            <div aria-hidden="true" style={{ position: "fixed", left: -10000, top: 0, width: "210mm", pointerEvents: "none" }}>
                {docs.map((doc) => (
                    <div key={doc.id} data-testid="send-document-page" ref={(node) => { pageRefs.current[doc.id] = node; }}>
                        <Template
                            data={doc}
                            companyProfile={companyProfile}
                            docType={docType}
                            taxRate={0}
                            forceNegativeTotals={forceNegativeTotals}
                        />
                    </div>
                ))}
            </div>,
            document.body
        )}
    </>;
}
